import { Button } from "../components/ui/button"
import { Card } from "../components/ui/card"
import { Spinner } from "../components/ui/spinner"
import { useToast } from "../components/ui/toast"
import { useLogout, useMe, useSettings } from "../lib/hooks"

// Phase 6 cleanup (user 確定 2026-06-13): Settings ページは read-only。
// API キーは `.env`、defaults は `configs/default.yaml` で admin が設定する。
// per-user API key は Phase 8 以降の検討。

const PROVIDERS = ["openai", "anthropic", "gemini"] as const

export function Settings() {
  const { data, isLoading, error } = useSettings()
  const me = useMe()
  const logout = useLogout()
  const toast = useToast()

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="size-6" />
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="px-20 pt-10">
        <Card className="p-6 text-sm text-danger">設定の読み込みに失敗しました</Card>
      </div>
    )
  }

  const user = me.data?.user ?? null
  const displayName = user ? user.display_name || user.email.split("@")[0] : null
  const initial = (displayName || user?.email || "?").charAt(0).toUpperCase()
  const roleLabel = user?.is_admin ? "Admin" : "Member"

  return (
    <div className="flex flex-col gap-7 pb-20 pt-10 px-20">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[28px] font-semibold leading-9 text-text-primary">Settings</h1>
        <p className="text-sm text-text-secondary">
          現在の設定 (アカウント / 既定値 / API キー有無) を表示します。変更は `.env` /
          `configs/default.yaml` 経由で admin が行います。
        </p>
      </div>

      {user ? (
        <Card className="max-w-3xl p-6">
          <h2 className="mb-4 text-base font-semibold text-text-primary">アカウント</h2>
          <div className="flex items-center gap-4">
            <div className="flex size-12 items-center justify-center rounded-full bg-accent text-xl font-semibold text-text-on-accent">
              {initial}
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-[15px] font-semibold text-text-primary">{displayName}</span>
                <span className="rounded-full bg-border-default px-2 py-0.5 text-[10px] font-medium text-text-secondary">
                  {roleLabel}
                </span>
              </div>
              <span className="text-[13px] text-text-muted">{user.email}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={logout.isPending}
              onClick={() =>
                logout.mutate(undefined, {
                  onError: (err) =>
                    toast.show({
                      title: "ログアウトに失敗しました",
                      description: err instanceof Error ? err.message : undefined,
                      variant: "error",
                    }),
                })
              }
            >
              {logout.isPending ? "ログアウト中…" : "ログアウト"}
            </Button>
          </div>
        </Card>
      ) : null}

      <Card className="max-w-3xl p-8">
        <h2 className="mb-4 text-base font-semibold text-text-primary">既定値 (read-only)</h2>
        <p className="mb-4 text-xs text-text-muted">
          Submit form の初期値。変更は `configs/default.yaml` を編集して再デプロイ。
        </p>
        <dl className="grid grid-cols-[160px_1fr] gap-y-3 text-sm">
          <dt className="text-text-secondary">LLM モデル</dt>
          <dd className="text-text-primary">{data.defaults.llm_model || "(未設定)"}</dd>
          <dt className="text-text-secondary">Focus プリセット</dt>
          <dd className="text-text-primary">{data.defaults.focus_preset || "(未設定)"}</dd>
        </dl>
      </Card>

      <Card className="max-w-3xl p-8">
        <h2 className="mb-4 text-base font-semibold text-text-primary">API キー (read-only)</h2>
        <p className="mb-4 text-xs text-text-muted">
          `.env` で admin が設定 (shared、全 user 共通)。キー本体は表示しません。
        </p>
        <dl className="grid grid-cols-[160px_1fr] gap-y-2 text-sm">
          {PROVIDERS.map((p) => (
            <Row key={p} provider={p} configured={data.api_keys_configured[p] === true} />
          ))}
        </dl>
      </Card>
    </div>
  )
}

function Row({ provider, configured }: { provider: string; configured: boolean }) {
  return (
    <>
      <dt className="text-text-secondary">{provider}</dt>
      <dd className={configured ? "text-text-primary" : "text-text-muted"}>
        {configured ? "設定済" : "未設定"}
      </dd>
    </>
  )
}
